import React, {useEffect, useState, useMemo} from "react";
import {Field, Form} from "react-final-form";
import {PrimaryLoadingButton} from "../../common/components/elements/button/PrimaryButton";
import styled from "styled-components";
import {
  FieldContainer,
  FormHeader,
  FormText,
} from "../../common/components/collections/form/forms";
import Input from "../../common/components/elements/input/Input";
import Address from "../../common/models/address";
import Error from "../../common/components/elements/Error";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import routes from "../../common/routes";
import Link from "../../common/components/modules/Link";
import AddressSearch from "../../common/components/elements/input/AddressSearchInput";
import useBillingAddressSearch from "../hooks/useBillingAddressSearch";
import SeparatorLabel from "src/core/common/components/SeparatorLabel";

function BillingAddressForm({loading, onSuccess, onFailure, errorMessage}) {
  const [submittedAddress, setSubmittedAddress] = useState(null);
  const [searchState, search] = useBillingAddressSearch();
  function onSubmit({addressLine2}) {
    const address = Address.fromPrototype(submittedAddress, {
      address_line2: addressLine2,
    });
    onSuccess({address});
    setSubmittedAddress(null);
  }

  function onChangeAddress() {
    setSubmittedAddress(null);
  }

  function onSelectAddress(address) {
    if (!address.isValid()) {
      onFailure("Please specify an address with a building number.");
    } else {
      setAddress(address);
    }
  }
  function setAddress(address) {
    setSubmittedAddress(address);
  }

  const [, authApi] = useAuthentication();
  const user = authApi.userProfile();
  const isLoggedIn = authApi.isLoggedIn();

  const [initialAddress] = useState(user ? user.getBillingAddress() : null);

  /* eslint-disable */
  useEffect(() => {
    if (initialAddress) {
      setAddress(initialAddress)
    }
  }, [initialAddress])
  /* eslint-enable */

  const initialValues = useMemo(
    () => ({
      address: initialAddress,
      addressLine2: initialAddress ? initialAddress.addressLine2 : undefined,
    }),
    [initialAddress]
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({handleSubmit}) => (
        <Container onSubmit={handleSubmit}>
          <FormHeader>Billing Address</FormHeader>
          <HeaderText alignment={"center"}>
            {errorMessage && <Error>{errorMessage}</Error>}
            <span>Please enter your billing address.</span>
            {!isLoggedIn && (
              <p>
                If you already have an account please{" "}
                <Link replace={false} to={{pathname: routes.login}}>
                  Login
                </Link>
                . If not you can{" "}
                <Link replace={false} to={{pathname: routes.signup}}>
                  Register
                </Link>
                .
              </p>
            )}
          </HeaderText>
          <div>
            <AddressSearch
              state={searchState}
              search={search}
              onChange={onChangeAddress}
              onSelect={onSelectAddress}
              initialAddress={initialAddress}
              key={initialAddress}
            />
          </div>
          <div>
            <SeparatorLabel>Optional</SeparatorLabel>
          </div>
          <div>
            <Field
              name="addressLine2"
              render={({input}) => (
                <FieldContainer>
                  <Input
                    {...input}
                    index={2}
                    placeholder={"Apt. No."}
                    data-cy="addressLine2"
                  />
                </FieldContainer>
              )}
            />
          </div>
          <CustomPrimaryLoadingButton
            type="submit"
            data-cy="shopNowButton"
            loading={loading}
            disabled={!submittedAddress}
            label="Save"
          />
        </Container>
      )}
    />
  );
}

const Container = styled.form`
  width: 450px;
  display: flex;
  flex-direction: column;
  > div {
    margin-bottom: ${({theme}) => theme.v2.spacing(4)};
    width: 100%;
  }
`;
const CustomPrimaryLoadingButton = styled(PrimaryLoadingButton)`
  margin-top: ${({theme}) => theme.v2.spacing(4)};
`;
const HeaderText = styled(FormText)`
  text-align: center;
  span:hover {
    text-decoration: none !important;
  }
  > div {
    font-size: 14px;
  }
  p {
    color: ${({theme}) => theme.v2.color.base.grey["600"]};
  }
  margin-top: 0;
  margin-bottom: ${({theme}) => theme.v2.spacing(4)};
`;

export default BillingAddressForm;
